import { ShieldCheck } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * Nombres amigables de `role-catalog.ts` (`apps/api`), no los códigos
 * técnicos: Administración = ADMIN, Operaciones = OPERATIONS, Cocina =
 * KITCHEN, Reparto = DELIVERY, Facturación = BILLING, Comercial = SALES,
 * Auditoría = AUDITOR. Los tres roles de cliente ya se nombran así en
 * `customers.ts` → paso "Dar acceso al portal": Encargado de Compras
 * (CLIENT_BUYER), Supervisor de Cliente (CLIENT_MANAGER) y Consulta de
 * Cliente (CLIENT_VIEWER). No incluye SUPER_ADMIN: es un rol técnico
 * inmutable que no se asigna en el uso diario. Sí se menciona explícitamente
 * en el paso "Roles del equipo interno": es el único rol con el permiso
 * `fiscal.config` ("Configuración fiscal" y "Puntos de venta").
 */
export const ROLES_MODULE: GuideModule = {
  id: 'roles',
  title: 'Roles y permisos',
  icon: ShieldCheck,
  requiredPermission: null,
  priority: 2,
  summary:
    'Tu rol determina qué módulos ves en el menú lateral y qué botones podés usar dentro de cada uno. Si te falta un módulo o un botón está deshabilitado, no es un error: es que tu rol no incluye ese permiso. Hay roles para el equipo interno y roles separados para las empresas que usan el portal del cliente.',
  steps: [
    {
      title: 'Entendé qué controla tu rol',
      description:
        'Un rol es un conjunto de permisos. No elegís tu rol vos mismo: te lo asigna un administrador al darte de alta o al editar tu usuario. Si necesitás acceso a algo que no tenés, pedíselo a un administrador en vez de suponer que es un error del sistema.',
      keyTerms: ['rol', 'permiso'],
    },
    {
      title: 'Roles del equipo interno',
      description:
        'Administración gestiona clientes, contratos, catálogo, pedidos, producción, usuarios y Cobranzas, incluida la anulación de recibos. Operaciones se enfoca en pedidos, producción y logística, sin acceso financiero. Cocina solo consulta producción y exporta planillas. Reparto ve su propia hoja de ruta y firma entregas. Facturación genera borradores, excluye remitos, emite comprobantes y registra e imputa recibos en Cobranzas, pero no puede anularlos: esa acción queda reservada a Administración y Superadministración. La "Configuración fiscal" (datos del emisor, certificados, ambiente) y los puntos de venta quedan reservados al rol técnico `SUPER_ADMIN`, ni siquiera Facturación ni Administración los ven. Comercial gestiona clientes y contratos, con lectura de pedidos, facturas y recibos, sin poder registrarlos ni anularlos. Auditoría solo tiene lectura de todo el sistema, sin poder modificar nada, pensada para revisiones y controles.',
      keyTerms: ['rol'],
    },
    {
      title: 'Roles del portal del cliente',
      description:
        'Las empresas usan tres roles separados, propios del portal del cliente: Encargado de Compras carga y gestiona los pedidos de su empresa; Supervisor de Cliente hace lo mismo y además gestiona a los encargados de compras y ve las facturas de su empresa; Consulta de Cliente solo puede ver pedidos, remitos y facturas, sin cargar ni modificar nada.',
      keyTerms: ['rol', 'acceso-al-portal'],
    },
    {
      title: 'Primeros pasos por rol',
      description:
        'Al iniciar sesión, cada rol aterriza en un punto de partida distinto, pensado para lo primero que hace ese rol cada día: Comercial entra directo a Clientes, para gestionar empresas y contratos. Reparto entra a Mis entregas, pensada para el celular, para ver qué le toca entregar. Cocina entra a Producción de hoy, con la planilla del día ya abierta. Facturación entra a Facturación. Administración, Operaciones y el rol Superadministrador siempre aterrizan en el Tablero del día, incluso si además tienen otro rol combinado: son roles de alcance general y el Tablero es su punto de partida natural. Cualquier otro rol, o ninguno de los anteriores, también cae en el Tablero del día como destino por defecto. Desde ese punto de partida sigués navegando normalmente por el menú lateral a cualquier otro módulo al que tengas acceso.',
      keyTerms: ['rol', 'tablero-del-dia', 'remito'],
    },
    {
      title: 'Rol del sistema vs. rol personalizado',
      description:
        'En "Roles y Permisos" (dentro de Configuración, con el permiso "Ver roles y permisos") cada fila de la matriz es un rol. Los roles del catálogo (Administración, Operaciones, Cocina, Reparto, Facturación, Comercial, Auditoría y los tres de portal) llevan la etiqueta "Rol del sistema" y son de solo lectura: no tienen botón "Editar" ni "Eliminar". Un rol personalizado es cualquier otro, creado a mano; sí se puede editar o eliminar.',
      keyTerms: ['rol-del-sistema', 'rol-personalizado'],
    },
    {
      title: 'Leé la matriz',
      description:
        'En pantallas anchas, cada columna es un permiso agrupado por recurso (por ejemplo "Usuarios", "Contratos"), con su descripción completa en el encabezado. Un ● en el cruce de una fila y una columna significa que ese rol tiene ese permiso; un — significa que no. En móvil, la misma información se ve como una tarjeta por rol, con los permisos agrupados por recurso en vez de en columnas.',
      keyTerms: [],
    },
    {
      title: 'Creá un rol personalizado',
      description:
        'Con el permiso "Crear y editar roles y su matriz de permisos" verés el botón "Nuevo rol". Completá "Código" (mayúsculas, números y guion bajo, tiene que empezar con una letra; no se puede cambiar después), "Nombre" y, opcionalmente, "Descripción". Marcá los permisos: un checkbox deshabilitado significa que vos mismo no tenés ese permiso, así que no podés otorgárselo a otro rol.',
      keyTerms: ['rol-personalizado'],
    },
    {
      title: 'Lo que nunca puede tener un rol personalizado',
      description:
        '"Crear y editar roles y su matriz de permisos", "Operar como otro usuario (soporte; siempre auditado)" y "Configurar puntos de venta y credenciales fiscales" nunca aparecen como opción al crear o editar un rol personalizado, ni siquiera si vos mismo los tenés: quedan reservados a los roles del sistema, para que gestionar roles, suplantar usuarios y configurar ARCA sigan siendo capacidades que no se pueden delegar sin control.',
      keyTerms: ['rol-personalizado'],
    },
    {
      title: 'No podés eliminar un rol en uso',
      description:
        'El botón "Eliminar" de un rol personalizado queda deshabilitado mientras tenga usuarios asignados. Quitá la asignación de esos usuarios (desde Usuarios, editándolos) antes de poder eliminarlo.',
      keyTerms: [],
    },
  ],
  states: [],
  faq: [
    {
      question: '¿Puedo cambiar mi propio rol?',
      answer:
        'No. Solo un administrador puede asignar o cambiar roles, tanto para el equipo interno como para los usuarios del portal del cliente.',
    },
    {
      question: '¿Por qué no veo un módulo que un compañero sí ve?',
      answer:
        'Porque tienen roles distintos. El menú lateral muestra solo los módulos para los que tu rol tiene permiso.',
    },
    {
      question: '¿Un usuario puede tener más de un rol?',
      answer:
        'Sí, un usuario puede tener varios roles asignados a la vez; sus permisos son la suma de todos ellos.',
    },
    {
      question: '¿Los roles del portal del cliente pueden ver algo del sistema interno?',
      answer:
        'No. Los roles de cliente solo dan acceso al portal del cliente, una dirección separada. Nunca ven el Tablero del día, Producción ni ningún módulo interno.',
    },
    {
      question: '¿Por qué no puedo editar ni eliminar un rol del sistema?',
      answer:
        'Porque los roles del catálogo son de solo lectura, para todo el mundo, incluso el Superadministrador. Si necesitás un conjunto de permisos distinto, creá un rol personalizado.',
    },
    {
      question: '¿Por qué "Crear y editar roles y su matriz de permisos" no aparece como opción al crear un rol personalizado, si yo lo tengo?',
      answer:
        'Porque "Crear y editar roles y su matriz de permisos", "Operar como otro usuario (soporte; siempre auditado)" y "Configurar puntos de venta y credenciales fiscales" nunca están disponibles para un rol personalizado, sin excepción. No es que te falte el permiso: es una restricción del propio sistema.',
    },
  ],
  tips: [
    'Si un botón aparece deshabilitado, pasá el mouse por encima: casi siempre te dice si es por permiso o por el estado actual de lo que estás viendo.',
    'Antes de pedirle a soporte que revise "un error", confirmá con un administrador si tu rol debería incluir ese permiso.',
  ],
};
